






















import Vue from 'vue'
import InfoIcon from '@/assets/svgs/info_icon.svg?inline'
import variables from '@/scss/variables.scss'

interface Breakpoint {
  breakpoint: string
  side: string
}

const sideValidator = (value: string) => ['top', 'left', 'bottom', 'right'].includes(value)
const breakpointValidator = (value: string) => Object.keys(variables).includes(value)

const ONE_THIRD = (1 / 3)
const TWO_THIRDS = (2 / 3)

const SIDE_PLACEMENTS = {
  right: { // Default placement
    anchor: 'top right',
    self: 'top left',
    offset: [8, 24]
  },
  left: {
    anchor: 'top left',
    self: 'top right',
    offset: [8, 24]
  },

  topleft: {
    anchor: 'top left',
    self: 'bottom left',
    offset: [24, 8]
  },
  topcenter: {
    anchor: 'top middle',
    self: 'bottom middle',
    offset: [0, 8]
  },
  topright: {
    anchor: 'top right',
    self: 'bottom right',
    offset: [24, 8]
  },

  bottomleft: {
    anchor: 'bottom left',
    self: 'top left',
    offset: [24, 8]
  },
  bottomcenter: {
    anchor: 'bottom middle',
    self: 'top middle',
    offset: [0, 8]
  },
  bottomright: {
    anchor: 'bottom right',
    self: 'top right',
    offset: [24, 8]
  }
}

interface Data {
  anchor: string
  self: string
  offset: number[]
}

interface Methods {
  updatePlacement: () => void
}

interface Computed {
  sortedBreakpoints: undefined | Breakpoint[]
}

interface Props {
  side: string
  breakpoints?: Breakpoint[]

}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: 'Tooltip',
  components: {
    InfoIcon
  },
  props: {
    side: {
      type: String,
      required: false,
      default: 'left',
      validator: sideValidator
    },
    breakpoints: {
      // List of { breakpoint: String, side: String}
      // where breakpoint is one of:
      //  xs, sm, md, lg, xl, or default
      // and side is one of the allowed values for the side prop
      type: Array,
      required: false,
      validator: (value) => {
        if (value) {
          for (let i = 0; i < value.length; i++) {
            const breakpoint = value[i] as Breakpoint
            if (!(breakpointValidator(breakpoint.breakpoint) && sideValidator(breakpoint.side))) {
              return false
            }
          }
        }
        return true
      }
    }
  },
  data: function () {
    return {
      ...SIDE_PLACEMENTS.right
    }
  },
  computed: {
    sortedBreakpoints: function () {
      if (this.breakpoints) {
        const sorted = (this.breakpoints as Breakpoint[]).slice()
        // Sort breakpoint props in descending order
        sorted.sort((a, b) => (
          variables[b.breakpoint] - variables[a.breakpoint]
        ))
        return sorted
      }
      return undefined
    }
  },
  methods: {
    updatePlacement () {
      const currentSize = window.innerWidth
      let placement = this.side
      if (this.sortedBreakpoints) {
        // Find largest breakpoint and use value from SIDE_PLACEMENTS
        for (let i = 0; i < this.sortedBreakpoints.length; i++) {
          const { side, breakpoint } = this.sortedBreakpoints[i]
          if (currentSize >= parseInt(variables[breakpoint])) {
            placement = side
            break
          }
        }
      }
      // If placement is top or bottom, find position on screen and
      //  pick left, center, or right using thirds of screen
      if (placement === 'top' || placement === 'bottom') {
        const left = (this.$refs.icon as Element).getBoundingClientRect().left
        const leftPositionPercentage = left / currentSize
        if (leftPositionPercentage < ONE_THIRD) {
          // On left third
          placement = `${placement}left`
        } else if (leftPositionPercentage < TWO_THIRDS) {
          // On center third
          placement = `${placement}center`
        } else {
          // On right third
          placement = `${placement}right`
        }
      }
      const { anchor, self, offset } = SIDE_PLACEMENTS[placement]
      this.anchor = anchor
      this.self = self
      this.offset = offset
    }
  },
  mounted () {
    this.updatePlacement()
    // Only add event listener if there are breakpoints
    if (this.sortedBreakpoints) window.addEventListener('resize', this.updatePlacement)
  },
  destroyed () {
    // Event listener is only added if there are breakpoints
    if (this.sortedBreakpoints) window.removeEventListener('resize', this.updatePlacement)
  }
})
